// Phase 44.07 — Premium credit toast "Hai ricevuto CHF 5".
// Trigger: utente con recipient_referral.granted=True e notified_at=null.
// One-shot per account, persistito via POST /me/credit-toast/ack.
//
// UX: bottom-center su mobile, top-right su desktop. Slide-up morbida.
// Auto-dismiss 6s, non bloccante. Mai per crediti admin/manuali.
import { useEffect, useState } from "react";
import { Sparkles, X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const AUTO_DISMISS_MS = 6000;

export default function CreditReferralToast() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [acked, setAcked] = useState(false);

  useEffect(() => {
    if (!user || acked) return;
    const rr = user.recipient_referral || {};
    if (rr.granted && !rr.notified_at && (user.credit_balance_chf || 0) > 0) {
      // Slight delay to avoid stacking with other on-load UI
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [user, acked]);

  // Auto-dismiss + ack
  useEffect(() => {
    if (!visible || closing) return;
    const t = setTimeout(() => doDismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, closing]);

  const ackBackend = async () => {
    try {
      await api.post("/me/credit-toast/ack");
      setAcked(true);
      try { await refresh?.(); } catch { /* silent */ }
    } catch { /* silent */ }
  };

  const doDismiss = async () => {
    if (closing) return;
    setClosing(true);
    await ackBackend();
    // wait CSS slide-out then hide
    setTimeout(() => { setVisible(false); setClosing(false); }, 260);
  };

  const useNow = async () => {
    await ackBackend();
    setVisible(false);
    navigate("/app/new");
  };

  if (!visible || !user) return null;

  const amount = Number(user.credit_balance_chf || 5).toFixed(0);
  return (
    <div
      className={`fixed z-[70] pointer-events-none flex justify-center sm:justify-end inset-x-0 sm:inset-x-auto sm:right-6 bottom-[calc(env(safe-area-inset-bottom,0px)+72px)] sm:bottom-6 px-4 sm:px-0 transition-all duration-300 ${closing ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"}`}
      data-testid="credit-referral-toast"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto w-full sm:max-w-sm rounded-2xl bg-dapnow-ink text-white shadow-2xl ring-1 ring-white/10 overflow-hidden relative">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-500/30 blur-2xl" />
        <div className="absolute -bottom-12 -left-10 w-32 h-32 rounded-full bg-dapnow-blue/25 blur-2xl" />
        <div className="relative flex items-start gap-3 p-4">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30 shrink-0">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-extrabold tracking-tight leading-tight" data-testid="credit-toast-title">
              Hai ricevuto CHF {amount}
            </p>
            <p className="text-xs text-white/80 mt-0.5 leading-snug">
              Credito DapNow aggiunto al tuo account grazie al referral.
            </p>
            <button
              type="button"
              onClick={useNow}
              data-testid="credit-toast-cta"
              className="mt-2.5 inline-flex items-center gap-1 rounded-lg bg-emerald-500/95 hover:bg-emerald-500 text-dapnow-ink px-3 h-8 text-[12px] font-extrabold tracking-tight transition active:scale-[0.97]"
            >
              Usalo ora <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={doDismiss}
            data-testid="credit-toast-close"
            className="rounded-md p-1 hover:bg-white/10 text-white/60 hover:text-white transition shrink-0"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
